import { safeProductUrl } from './safeProductUrl';
import {
  SamplingProductListCloseIcon,
  SamplingTooltipArrowIcon
} from '@/components/common/icon';
import React from 'react';
import { Button, Tooltip } from 'aha-components';
import { useTranslation } from 'react-i18next';
import { isMobile } from '@/biz/tool';
import { normalizePrice } from '@/utils/normalizePrice';
import BottomDrawer from '../BottomDrawer';
import WebModal from '../Modal';
import type { PhysicalSamplingConfig } from './types';

interface ProductListModalProps {
  config: PhysicalSamplingConfig;
  visible: boolean;
  onClose: () => void;
  brandName?: string;
  brandLogoUrl?: string;
}

const ProductDetailLink: React.FC<{
  href: string;
  className: string;
  children: React.ReactNode;
}> = ({ href, className, children }) =>
  safeProductUrl(href) ? (
    <a
      href={safeProductUrl(href) || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className={`${className} text-[#737373] no-underline hover:text-[#737373] hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ff621d] focus-visible:outline-offset-2`}
    >
      {children}
    </a>
  ) : (
    <div className={className}>{children}</div>
  );

const ProductListContent: React.FC<{
  config: PhysicalSamplingConfig;
  label: string;
  brandName?: string;
  brandLogoUrl?: string;
  isDrawer: boolean;
}> = ({ config, label, brandName, brandLogoUrl, isDrawer }) => (
  <div
    className={`min-h-0 flex-1 overflow-y-auto py-6 ${isDrawer ? 'px-0' : 'px-6'} [font-family:'Sampling_Inter',Inter,sans-serif]`}
  >
    <div role="list" aria-label={label} className="flex flex-col gap-4">
      {config.products.map((product) => (
        <div
          key={product.id}
          role="listitem"
          className="flex min-w-0 items-start gap-3"
        >
          <ProductDetailLink
            href={product.productUrl}
            className="flex h-[68px] w-[68px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-solid border-[#E5E5E5] bg-white"
          >
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-full w-full object-contain p-1.5"
            />
          </ProductDetailLink>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="text-base font-semibold leading-6 text-[#171717]">
              {product.retailPriceUsd === null
                ? '—'
                : normalizePrice(product.retailPriceUsd)}
            </div>
            <Tooltip
              className="!block w-full"
              tooltipClassName="[&.aha-tooltip]:!rounded-lg [&.aha-tooltip]:!bg-[#0a0a0a] [&.aha-tooltip]:!px-3 [&.aha-tooltip]:!py-2 [&.aha-tooltip]:!text-xs [&.aha-tooltip]:!font-semibold [&.aha-tooltip]:!leading-[18px] [&.aha-tooltip]:![font-family:'Sampling_Inter',Inter,sans-serif] [&.aha-tooltip]:!shadow-[0_12px_16px_-4px_rgb(0_0_0_/_8%),0_4px_6px_-2px_rgb(0_0_0_/_3%),0_2px_2px_-1px_rgb(0_0_0_/_4%)]"
              position="top"
              width={300}
              gap={6}
              showArrow={false}
              content={
                <>
                  <span>{product.name}</span>
                  <div className="absolute left-1/2 top-full h-1.5 w-4 -translate-x-1/2">
                    <SamplingTooltipArrowIcon className="absolute bottom-0" />
                  </div>
                </>
              }
            >
              <ProductDetailLink
                href={product.productUrl}
                className="block w-full truncate text-xs font-normal leading-[18px] text-[#737373]"
              >
                {product.name}
              </ProductDetailLink>
            </Tooltip>
            {(brandName || brandLogoUrl) && (
              <div className="flex min-w-0 items-center gap-1">
                {brandLogoUrl && (
                  <img
                    src={brandLogoUrl}
                    alt=""
                    className="h-4 w-4 shrink-0 rounded object-cover"
                  />
                )}
                {brandName && (
                  <span className="truncate text-sm font-normal leading-5 text-[#737373]">
                    {brandName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProductListModal: React.FC<ProductListModalProps> = ({
  config,
  visible,
  onClose,
  brandName,
  brandLogoUrl
}) => {
  const { t } = useTranslation();
  const title = t('physical_product.sampling.product_list_title', {
    defaultValue: 'Product list'
  });
  const useDrawer = isMobile();
  const content = (
    <ProductListContent
      config={config}
      label={title}
      brandName={brandName}
      brandLogoUrl={brandLogoUrl}
      isDrawer={useDrawer}
    />
  );

  if (useDrawer) {
    return (
      <BottomDrawer
        visible={visible}
        title={title}
        handleClose={onClose}
        handleSubmit={onClose}
        okText={t('common.ok', { defaultValue: 'OK' })}
        className="max-h-[82dvh] rounded-t-2xl"
        contentClassName="min-h-0"
        paperStyle={{
          padding: '24px 24px calc(24px + env(safe-area-inset-bottom, 0px))'
        }}
      >
        {content}
      </BottomDrawer>
    );
  }

  return (
    <WebModal
      manageFocus
      ariaLabel={title}
      visible={visible}
      handleClose={onClose}
      isShowHeader={false}
      isShowFooter={false}
      canClose
      width={540}
      style={{ height: 680, maxHeight: 'calc(100dvh - 48px)' }}
      className="!items-stretch !justify-start !overflow-hidden !rounded-lg !p-0"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex h-[68px] shrink-0 items-center justify-between border-b border-solid border-[#E5E5E5] px-6 py-4">
          <h2
            className="m-0 font-['DM_Sans'] text-2xl font-semibold leading-9 tracking-[-0.24px] text-[#292524]"
            style={{ fontVariationSettings: '"opsz" 14' }}
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-[#404040]"
            onClick={onClose}
          >
            <SamplingProductListCloseIcon />
          </button>
        </div>
        {content}
        <div className="flex h-[88px] shrink-0 items-center justify-end gap-[13px] bg-white px-6 py-6 shadow-[0_-6px_8px_rgba(0,0,0,0.05)]">
          <Button
            className="!h-10 !w-[100px] relative !rounded-lg !text-sm !font-semibold !leading-5 ![font-family:'Sampling_Inter',Inter,sans-serif] !shadow-[0_1px_2px_rgb(0_0_0_/_5%)] !border !border-solid !border-[#d4d4d4] !text-[#404040]"
            onClick={onClose}
          >
            {t('common.modal.cancel_btn', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="primary"
            className="!h-10 !w-[100px] relative !rounded-lg !text-sm !font-semibold !leading-5 ![font-family:'Sampling_Inter',Inter,sans-serif] !shadow-[0_1px_2px_rgb(0_0_0_/_5%)] !border-2 !border-solid !border-[rgb(255_255_255_/_12%)] !bg-[#ff621d] after:pointer-events-none after:absolute after:-inset-0.5 after:rounded-[inherit] after:shadow-[inset_0_0_0_1px_rgb(0_0_0_/_18%),inset_0_-2px_0_rgb(0_0_0_/_5%)] after:content-['']"
            onClick={onClose}
          >
            {t('common.ok', { defaultValue: 'OK' })}
          </Button>
        </div>
      </div>
    </WebModal>
  );
};

export default ProductListModal;
